import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BusinessTrip,
  BusinessTripExpense,
  ExpenseTotals,
} from "types/BusinessTrip";
import {
  fetchBusinessTripById,
  fetchTripExpenses,
} from "services/business-trip.service";
import { AppDispatch } from "configureStore";

interface BusinessTripContainerState {
  isLoading: boolean;
  isError: boolean;
  errMsg: string;
  businessTripEntity: BusinessTrip | null;
  // The trip id of the latest in-flight request. Responses (success or error)
  // only apply if they still match it, so a stale response for a trip we've
  // navigated away from can't overwrite the current one or surface a wrong error.
  requestedTripId: string | null;
  expenses: BusinessTripExpense[];
  expenseTotals: ExpenseTotals | null;
  expensesLoading: boolean;
}

const initialState: BusinessTripContainerState = {
  isLoading: false,
  isError: false,
  errMsg: "",
  businessTripEntity: null,
  requestedTripId: null,
  expenses: [],
  expenseTotals: null,
  expensesLoading: false,
};

const BusinessTripContainerSlice = createSlice({
  name: "businessTripContainer",
  initialState,
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(
      state: BusinessTripContainerState,
      action: PayloadAction<boolean>
    ) {
      state.isLoading = action.payload;
    },
    // Marks the start of a fetch: records which trip is requested, shows
    // loading, and clears any prior error so a stale error can't block the new
    // trip.
    setRequest(
      state: BusinessTripContainerState,
      action: PayloadAction<string>
    ) {
      state.requestedTripId = action.payload;
      state.isLoading = true;
      state.isError = false;
      state.errMsg = "";
    },
    setError(
      state: BusinessTripContainerState,
      action: PayloadAction<{ id: string; error: string }>
    ) {
      if (state.requestedTripId !== action.payload.id) {
        return;
      }
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setBusinessTripContainer(
      state: BusinessTripContainerState,
      action: PayloadAction<{ id: string; data: BusinessTrip | null }>
    ) {
      if (state.requestedTripId !== action.payload.id) {
        return;
      }
      state.businessTripEntity = action.payload.data;
      state.isLoading = false;
      state.isError = false;
      state.errMsg = "";
    },
    setExpensesLoading(
      state: BusinessTripContainerState,
      action: PayloadAction<boolean>
    ) {
      state.expensesLoading = action.payload;
    },
    setExpenses(
      state: BusinessTripContainerState,
      action: PayloadAction<{
        expenses: BusinessTripExpense[];
        totals: ExpenseTotals;
      }>
    ) {
      state.expenses = action.payload.expenses;
      state.expenseTotals = action.payload.totals;
      state.expensesLoading = false;
    },
  },
});

const { actions, reducer } = BusinessTripContainerSlice;

export const {
  setDestroy,
  setLoading,
  setRequest,
  setError,
  setBusinessTripContainer,
  setExpensesLoading,
  setExpenses,
} = actions;
export default reducer;

export const fetchBusinessTripDetails =
  ({
    tripId,
    companyId,
  }: {
    tripId: string;
    companyId: string;
  }) =>
  async (dispatch: AppDispatch) => {
    dispatch(setRequest(tripId));
    try {
      const entity = await fetchBusinessTripById(tripId, companyId);
      dispatch(setBusinessTripContainer({ id: tripId, data: entity }));
    } catch (e) {
      const error = "not_fetch";
      dispatch(setError({ id: tripId, error }));
      return false;
    }
  };

export const fetchBusinessTripExpenses =
  ({ tripId, companyId }: { tripId: string; companyId: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(setExpensesLoading(true));
    try {
      const data = await fetchTripExpenses(companyId, tripId);
      dispatch(
        setExpenses({ expenses: data.expenses, totals: data.totals })
      );
    } catch (e) {
      dispatch(
        setExpenses({
          expenses: [],
          totals: {
            totalEur: 0,
            advanceMoney: 0,
            difference: 0,
            byCategory: {},
          },
        })
      );
      dispatch(setExpensesLoading(false));
    }
  };
